import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import Guid from "@/app/types/common/guid";
import { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import type { EnumSalesVisitAssociatedEntityType } from "@/app/services/generated/enums/enumSalesVisitAssociatedEntityType.generated";
import { LocalDate } from "@js-joda/core";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";

const builder = new RouteBuilder("Sales_");

export const salesRoutes = {
  createOrder: builder.routeWithQuery(
    {
      url: "/orders/create-order",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "orders" */ "@/pages/sales/orders/CreateOrderPage.vue"
        ),
      componentProps: (route) => ({
        outletId: Guid.tryParse(route.query?.outletId?.toString()),
        salesVisitLogId: Guid.tryParse(
          route.query?.salesVisitLogId?.toString()
        ),
      }),
      requiredUserPermissions: [EnumUserPermission.ordersEdit],
    },
    (opts: { outletId?: Guid | null; salesVisitLogId?: Guid | null }) => ({
      outletId: opts.outletId?.toString(),
      salesVisitLogId: opts.salesVisitLogId?.toString(),
    })
  ),
};

const menuNames = {
  orders: {
    list: "orders_list",
    view: "view_order",
    edit: "edit_order",
    old_create: "old-create-order",
    old_view: "old-view-order",
    old_edit: "old-edit-order",
  },
};

export const salesMenu = {
  root: "navigation_sales_group",
  listOrders: () => ({
    name: menuNames.orders.list,
  }),
  viewOrder: (
    id: Guid,
    opts?: { salesVisitLogId?: Guid | null }
  ): Location => ({
    name: menuNames.orders.view,
    params: {
      id: id.toString(),
      salesVisitLogId: opts?.salesVisitLogId?.toString() ?? "",
    },
  }),
  editOrder: (
    id: Guid,
    opts?: { salesVisitLogId?: Guid | null }
  ): Location => ({
    name: menuNames.orders.edit,
    params: {
      id: id.toString(),
      salesVisitLogId: opts?.salesVisitLogId?.toString() ?? "",
    },
  }),
  oldCredits: {
    create: "add_lines_to_credit",
    view: "view_credit",
    details: "credit_details",
  },
  creditNotes: {
    list: "credit_notes_list",
    setup: "credit_notes_set_up",
    edit: "edit_credit_note",
    create: "create_credit_note",
    view: "view_credit_note",
  },
  createCreditNote: (opts?: {
    outletId?: Guid | null;
    ullageId?: Guid | null;
    despatchDate?: LocalDate | null;
  }): Location => ({
    name: salesMenu.creditNotes.create,
    params: {
      outletId: opts?.outletId?.toString() ?? "",
      ullageId: opts?.ullageId?.toString() ?? "",
      despatchDate: opts?.despatchDate?.toString() ?? "",
    },
  }),
  viewCreditNote: (id: Guid): Location => ({
    name: salesMenu.creditNotes.view,
    params: { id: id.toString() },
  }),
  editCreditNote: (id: Guid): Location => ({
    name: salesMenu.creditNotes.edit,
    params: { id: id.toString() },
  }),
  oldViewCredit: (id: Guid): Location => ({
    name: salesMenu.oldCredits.view,
    params: { id: id.toString() },
  }),
  listCreditNotes: (): Location => ({ name: salesMenu.creditNotes.list }),
  leads: {
    root: "leads_leads",
    pipeline: "leads_pipeline",
    list: "leads_list",
    edit: "leads_edit",
    create: "leads_create",
    finder: "leads_finder",
    viewLeads: (): Location => ({
      name: salesMenu.leads.list,
    }),
    createLead: () => ({
      name: salesMenu.leads.create,
    }),
    createLeadFromLeadsMap: (opts: {
      businessName: string;
      telephoneNumber: string;
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      addressLine4: string;
      addressLine5: string;
      addressLine6: string;
      addressPostcode: string;
      webAddress: string;
    }): Location => ({
      name: salesMenu.leads.create,
      query: opts,
    }),
    viewLead: (leadId: Guid): Location => ({
      name: salesMenu.leads.edit,
      params: {
        id: leadId.toString(),
      },
    }),
  },
  salesVisits: {
    root: "navigation_sales_visits_group",
    planned: {
      list: "planned_sales_visit_list",
      listLocation: (): Location => ({
        name: salesMenu.salesVisits.planned.list,
      }),
      edit: "planned_sales_visit_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: salesMenu.salesVisits.planned.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (): Location => ({
        name: salesMenu.salesVisits.planned.edit,
      }),
    },
    logs: {
      list: "sales_visit_log_list",
      listLocation: (): Location => ({
        name: salesMenu.salesVisits.logs.list,
      }),
      edit: "sales_visit_log_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: salesMenu.salesVisits.logs.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (opts?: {
        entityType: EnumSalesVisitAssociatedEntityType;
        entityId: Guid;
      }): Location => ({
        name: salesMenu.salesVisits.logs.edit,
        query: {
          entityType: opts?.entityType.key.toString(),
          entityId: opts?.entityId?.toString(),
        },
      }),
    },
    visitPlan: "sales_visit_plan",
  },
};

export const buildSalesMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: salesMenu.root,
    icon: "shopping_basket",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuSalesView]),
    subMenus: [
      MenuItem.fromOpts({
        name: menuNames.orders.list,
        icon: "list",
        url: "/orders/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/components/pages/orders/ListOrders.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.ordersView],
      }),
      MenuItem.forRoute("", salesRoutes.createOrder, {
        visible: false,
      }),
      MenuItem.fromOpts({
        name: menuNames.orders.view,
        url: "/orders/view-order/:id",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/orders/ViewOrderPage.vue"
          ),
        componentProps: (route) => ({
          id: Guid.tryParse(route.params.id),
          salesVisitLogId: Guid.tryParse(route.params.salesVisitLogId),
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.ordersView],
      }),
      MenuItem.fromOpts({
        name: menuNames.orders.edit,
        url: "/orders/edit-order/:id",
        breadcrumbParentName: menuNames.orders.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/orders/EditOrderPage.vue"
          ),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.ordersEdit],
        componentProps: (route) => ({
          id: Guid.tryParse(route.params.id),
          salesVisitLogId: Guid.tryParse(route.params.salesVisitLogId),
        }),
      }),
      MenuItem.fromOpts({
        name: salesMenu.oldCredits.create,
        url: "/credit/:id/addLine",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "credit" */ "@/components/pages/orders/creditnotes/AddLine.vue"
          ),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.fromOpts({
        name: salesMenu.oldCredits.view,
        url: "/credit/:id/view",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "credit" */ "@/components/pages/orders/creditnotes/ViewCredit.vue"
          ),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.list,
        icon: "list",
        url: "/creditnotes/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/components/pages/orders/creditnotes/ListCreditNotes.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.setup,
        url: "/creditnotes/setup",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/components/pages/orders/creditnotes/SetupCreditNote.vue"
          ),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.fromOpts({
        name: salesMenu.oldCredits.details,
        url: "/credit/:id/details",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/components/pages/orders/creditnotes/CreditDetails.vue"
          ),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.create,
        url: "/creditnotes/create-credit-note",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/creditnotes/CreateCreditNotePage.vue"
          ),
        componentProps: (route) => ({
          outletId: Guid.tryParse(route.params.outletId),
          ullageId: Guid.tryParse(route.params.ullageId),
          despatchDate: route.params.despatchDate
            ? LocalDate.parse(route.params.despatchDate)
            : null,
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesEdit],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.edit,
        url: "/creditnotes/edit-credit-note/:id",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/creditnotes/EditCreditNotePage.vue"
          ),
        componentProps: (route) => ({
          id: Guid.tryParse(route.params.id),
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesEdit],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.view,
        url: "/creditnotes/view-credit-note/:id",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/creditnotes/ViewCreditNotePage.vue"
          ),
        componentProps: (route) => ({
          id: Guid.tryParse(route.params.id),
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.forBranch({
        name: salesMenu.leads.root,
        subMenus: [
          MenuItem.fromOpts({
            name: salesMenu.leads.list,
            url: "/leads/list",
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/ListLeads.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.pipeline,
            url: "/leads/pipeline",
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/LeadsPipeline.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.finder,
            url: "/leads/map",
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/LeadsFinder.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.create,
            url: "/leads/create",
            breadcrumbParentName: salesMenu.leads.list,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/EditLead.vue"
              ),
            visible: false,
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.edit,
            url: "/leads/view/:id",
            breadcrumbParentName: salesMenu.leads.list,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/EditLead.vue"
              ),
            visible: false,
            componentProps: (route) => ({
              id: route.params.id,
            }),
          }),
        ],
      }),

      MenuItem.forBranch({
        name: salesMenu.salesVisits.root,
        subMenus: [
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.planned.list,
            url: "/sales/sales-visits/planned-visits",
            breadcrumbParentName: salesMenu.salesVisits.root,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/ListPlannedSalesVisits.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.planned.edit,
            url: "/sales/sales-visits/planned-visit/:id?/:mode?",
            breadcrumbParentName: salesMenu.salesVisits.planned.list,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/EditPlannedSalesVisit.vue"
              ),
            visible: false,
          }),
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.logs.list,
            url: "/sales/sales-visits/logs",
            breadcrumbParentName: salesMenu.salesVisits.root,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/ListSalesVisitLogs.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.logs.edit,
            url: "/sales/sales-visits/log/:id?/:mode?",
            breadcrumbParentName: salesMenu.salesVisits.logs.list,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/EditSalesVisitLog.vue"
              ),
            visible: false,
          }),
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.visitPlan,
            url: "/sales/sales-visits/plan",
            breadcrumbParentName: salesMenu.salesVisits.root,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/SalesVisitPlan.vue"
              ),
          }),
        ],
      }),
    ],
  }),
];
